import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { Grid, Paper, Stack } from '@mui/material'
import { ButtonSecondary } from 'components/ButtonSecondary'
import FormInput from 'components/FormInput'
import InputMask from 'components/FormInputMask'
import { FormInputSelect } from 'components/FormInputSelect'
import React, { useEffect } from 'react'
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { HearingCircumstancesType } from 'types/hearing-circumstances.type'
import { useAddress } from 'hooks/address.hook'
import FormInputCep from 'components/FormInputCEP'

type AddressGroupProps = {
  control: Control<HearingCircumstancesType>
  watch: UseFormWatch<HearingCircumstancesType>
  setValue: UseFormSetValue<HearingCircumstancesType>
}

export const AddressGroup: React.FC<AddressGroupProps> = ({ control, watch, setValue }) => {
  const zipCodeValue = watch(`endereco.cep`)
  const stateIdValue = watch(`endereco.ufId`)
  const cityIdValue = watch(`endereco.cidadeId`)

  const {
    address: { stateOptions, cityOptions, neighborhoodOptions },
    getAddressByZipCode,
  } = useAddress(stateIdValue, cityIdValue)

  const handleFillAddressFields = async (): Promise<void> => {
    if (zipCodeValue) {
      const res = await getAddressByZipCode(zipCodeValue)
      if (!res) return
      const neighborhoodValue = res.bairroId ? res.bairroId : '0'
      const cityValue = res.cidadeId ? res.cidadeId : '0'
      setValue(`endereco.cep`, res.cep)
      setValue(`endereco.ufId`, res.ufId)
      setValue(`endereco.cidadeId`, cityValue)
      setValue(`endereco.bairroId`, neighborhoodValue)
      setValue(`endereco.logradouro`, res.logradouro)
      setValue(`endereco.complemento`, res.complemento)
    }
  }

  const handleDisableSearchZipCode = (): boolean => {
    let disabled = true
    if (zipCodeValue && zipCodeValue.length >= 7) {
      disabled = false
    }
    return disabled
  }

  useEffect(() => {
    if (stateIdValue && stateIdValue === '0') {
      setValue(`endereco.cidadeId`, '0')
      setValue(`endereco.bairroId`, '0')
    } else if (cityIdValue && cityIdValue === '0') {
      setValue(`endereco.bairroId`, '0')
    }
  }, [setValue, stateIdValue, cityIdValue])

  return (
    <Paper variant='outlined' sx={{ ml: 2, mb: 2, p: 3, width: '100%' }}>
      <Grid container spacing={2} rowSpacing={4}>
        <Grid item md={6} xs={12}>
          <Stack direction='row' spacing={1}>
            <FormInputCep
              control={control}
              name='endereco.cep'
              label='CEP'
              placeholder='Informe o CEP'
              sx={{ flex: 1 }}
            />
            <ButtonSecondary
              maxHeight={40}
              startIcon={<SearchRoundedIcon />}
              label='Consultar'
              disabled={handleDisableSearchZipCode()}
              onClick={handleFillAddressFields}
            />
          </Stack>
        </Grid>
        <Grid item md={3} xs={12}>
          <FormInputSelect
            control={control}
            name='endereco.ufId'
            label='Estado'
            options={stateOptions}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <FormInputSelect
            control={control}
            name='endereco.cidadeId'
            label='Cidade'
            options={cityOptions}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <FormInputSelect
            control={control}
            name='endereco.bairroId'
            label='Bairro'
            options={neighborhoodOptions}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInput
            control={control}
            name='endereco.logradouro'
            label='Logradouro'
            placeholder='Informe o Logradouro'
            maxLength={60}
          />
        </Grid>
        <Grid item md={2} xs={12}>
          <InputMask control={control} name='endereco.numero' label='N°' mask='0000000000' />
        </Grid>
        <Grid item md={3} xs={12}>
          <FormInput
            control={control}
            name='endereco.complemento'
            label='Complemento'
            placeholder='Informe o complemento'
            maxLength={20}
          />
        </Grid>
      </Grid>
    </Paper>
  )
}
